import { ChartOutput, UserPreferences, ChartLayoutPreference, AyanamsaPreference, ThemePreference } from '../../../../packages/types/src/index';

export const getApiBaseUrl = (): string => {
  const envUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000/api/v1';
  const cleanUrl = envUrl.replace(/\/+$/, '');
  if (!cleanUrl.endsWith('/api/v1')) {
    return `${cleanUrl}/api/v1`;
  }
  return cleanUrl;
};

export const API_BASE_URL = getApiBaseUrl();


export interface CreateBirthProfileInput {
  name: string;
  birthDate: string;
  birthTime: string;
  latitude: number;
  longitude: number;
  timezone: string;
  locationName?: string;
  ayanamsa?: AyanamsaPreference;
}

export interface BirthProfileRecord {
  id: string;
  userId?: string | null;
  name: string;
  birthDate: string;
  birthTime: string;
  latitude: number;
  longitude: number;
  timezone: string;
  locationName?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ChartRecord {
  id: string;
  birthProfileId: string;
  ayanamsa?: AyanamsaPreference;
  chartLayout?: ChartLayoutPreference;
  fingerprint?: string;
  chartOutput: ChartOutput;
  schemaVersion: string;
  engineVersion: string;
  calculationConfig: any;
  ephemerisInfo: any;
  createdAt: string;
}

export interface UserRecord {
  id: string;
  email: string;
  createdAt?: string;
}

export class ApiError extends Error {
  statusCode: number;
  errorCode?: string;
  correlationId?: string;

  constructor(message: string, statusCode: number, errorCode?: string, correlationId?: string) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.errorCode = errorCode;
    this.correlationId = correlationId;
  }
}

async function requestJson<T>(url: string, options?: RequestInit): Promise<T> {
  const reqOptions: RequestInit = {
    credentials: 'include', // Include HTTP-only cookie
    cache: 'no-store', // Prevent Next.js App Router / fetch cache from storing stale responses
    ...options
  };

  const res = await fetch(url, reqOptions);
  if (!res.ok) {
    let errorMsg = `API request failed with status ${res.status}`;
    let code: string | undefined;
    let correlationId: string | undefined;

    try {
      const errorJson = await res.json();
      if (errorJson.error) {
        errorMsg = Array.isArray(errorJson.error.message)
          ? errorJson.error.message.join(', ')
          : errorJson.error.message || errorMsg;
        code = errorJson.error.code;
      } else if (errorJson.message) {
        errorMsg = Array.isArray(errorJson.message) ? errorJson.message.join(', ') : errorJson.message;
      }
      correlationId = errorJson.correlationId || res.headers.get('x-correlation-id') || undefined;
    } catch {
      // Ignore JSON parse error
    }

    throw new ApiError(errorMsg, res.status, code, correlationId);
  }
  return res.json();
}

export async function registerApi(email: string, password: string): Promise<{ success: boolean; user: UserRecord }> {
  return requestJson<{ success: boolean; user: UserRecord }>(`${API_BASE_URL}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password })
  });
}

export async function loginApi(email: string, password: string): Promise<{ success: boolean; user: UserRecord }> {
  return requestJson<{ success: boolean; user: UserRecord }>(`${API_BASE_URL}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password })
  });
}

export async function logoutApi(): Promise<{ success: boolean }> {
  return requestJson<{ success: boolean }>(`${API_BASE_URL}/auth/logout`, {
    method: 'POST'
  });
}

export async function getMeApi(): Promise<{ success: boolean; user: UserRecord }> {
  return requestJson<{ success: boolean; user: UserRecord }>(`${API_BASE_URL}/auth/me`);
}

export async function createBirthProfile(input: CreateBirthProfileInput): Promise<BirthProfileRecord> {
  return requestJson<BirthProfileRecord>(`${API_BASE_URL}/birth-profiles`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input)
  });
}

export async function listBirthProfiles(): Promise<BirthProfileRecord[]> {
  return requestJson<BirthProfileRecord[]>(`${API_BASE_URL}/birth-profiles`);
}

export async function createChart(birthProfileId: string, transitDate?: string, ayanamsa?: AyanamsaPreference): Promise<ChartRecord> {
  return requestJson<ChartRecord>(`${API_BASE_URL}/charts`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ birthProfileId, transitDate, ayanamsa })
  });
}

export async function calculateTransientChartApi(input: CreateBirthProfileInput): Promise<ChartRecord> {
  return requestJson<ChartRecord>(`${API_BASE_URL}/charts/transient`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      birth_date: input.birthDate,
      birth_time: input.birthTime,
      latitude: input.latitude,
      longitude: input.longitude,
      timezone: input.timezone,
      location_name: input.locationName,
      ayanamsa: input.ayanamsa || 'lahiri'
    })
  });
}

export async function getChart(chartId: string): Promise<ChartRecord> {
  return requestJson<ChartRecord>(`${API_BASE_URL}/charts/${chartId}`);
}

export async function updateChartLayout(chartId: string, layout: ChartLayoutPreference): Promise<ChartRecord> {
  return requestJson<ChartRecord>(`${API_BASE_URL}/charts/${chartId}/layout`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ layout })
  });
}

export async function enableChartSharing(chartId: string): Promise<ChartRecord> {
  return requestJson<ChartRecord>(`${API_BASE_URL}/charts/${chartId}/share`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ enabled: true })
  });
}

export async function revokeChartSharing(chartId: string): Promise<ChartRecord> {
  return requestJson<ChartRecord>(`${API_BASE_URL}/charts/${chartId}/share`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ enabled: false })
  });
}

export async function getPublicSharedChart(chartId: string): Promise<ChartRecord> {
  return requestJson<ChartRecord>(`${API_BASE_URL}/public/charts/${chartId}`);
}

export async function getBirthProfile(profileId: string): Promise<BirthProfileRecord> {
  return requestJson<BirthProfileRecord>(`${API_BASE_URL}/birth-profiles/${profileId}`);
}

export async function deleteBirthProfile(profileId: string): Promise<{ success: boolean; message: string }> {
  return requestJson<{ success: boolean; message: string }>(`${API_BASE_URL}/birth-profiles/${profileId}`, {
    method: 'DELETE'
  });
}

export async function getBirthProfileCharts(profileId: string): Promise<ChartRecord[]> {
  return requestJson<ChartRecord[]>(`${API_BASE_URL}/birth-profiles/${profileId}/charts`);
}

export async function checkApiHealth(): Promise<any> {
  return requestJson<any>(`${API_BASE_URL}/health`);
}

export async function generateInterpretation(
  chartId: string,
  section: string,
  forceRegenerate?: boolean
): Promise<any> {
  return requestJson<any>(`${API_BASE_URL}/ai/interpretations`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ chartId, section, forceRegenerate })
  });
}

export async function getInterpretation(id: string): Promise<any> {
  return requestJson<any>(`${API_BASE_URL}/ai/interpretations/${id}`);
}

export async function listChartInterpretations(chartId: string): Promise<any[]> {
  return requestJson<any[]>(`${API_BASE_URL}/charts/${chartId}/interpretations`);
}

export async function getUserPreferences(): Promise<UserPreferences> {
  return requestJson<UserPreferences>(`${API_BASE_URL}/users/me/preferences`);
}

export async function updateUserPreferences(updates: {
  default_chart_layout?: ChartLayoutPreference;
  default_ayanamsa?: AyanamsaPreference;
  theme?: ThemePreference;
}): Promise<UserPreferences> {
  return requestJson<UserPreferences>(`${API_BASE_URL}/users/me/preferences`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates)
  });
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<{ success: boolean; message: string }> {
  return requestJson<{ success: boolean; message: string }>(`${API_BASE_URL}/auth/change-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ currentPassword, newPassword })
  });
}

export interface KootaScore {
  name: string;
  score: number;
  maxScore: number;
  description: string;
  id?: string;
  details?: Record<string, any>;
}

export interface MatchingDoshas {
  nadiDosha: boolean;
  nadiDoshaExceptionApplies?: boolean;
  bhakootDosha: boolean;
  manglikA: boolean;
  manglikB: boolean;
}

export interface MatchingResult {
  totalScore: number;
  maxScore: number;
  kootas: KootaScore[];
  doshas: MatchingDoshas;
  verdict: string;
  verdictCode?: string;
}

export async function calculateKundliMatching(payload: {
  chartIdA?: string;
  chartIdB?: string;
  personA?: CreateBirthProfileInput;
  personB?: CreateBirthProfileInput;
}): Promise<MatchingResult> {
  return requestJson<MatchingResult>(`${API_BASE_URL}/matching`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
}

export interface HoroscopeParsedContent {
  schemaVersion?: string;
  sign?: string;
  date?: string;
  summary?: string;
  sections?: {
    general?: string;
    career?: string;
    relationships?: string;
    health?: string;
  };
  disclaimer?: string;
}

export interface DailyHoroscopeRecord {
  id: string;
  sign: string;
  horoscopeDate: string;
  period?: string;
  periodKey?: string;
  isPending?: boolean;
  content: string;
  factsSnapshot: any;
  modelInfo: any;
  createdAt: string;
}

export async function getDailyHoroscope(
  sign: string,
  period: 'daily' | 'weekly' | 'monthly' | 'yearly' = 'daily'
): Promise<DailyHoroscopeRecord> {
  return requestJson<DailyHoroscopeRecord>(`${API_BASE_URL}/horoscope/${period}/${encodeURIComponent(sign)}`);
}

export async function listDailyHoroscopes(
  period: 'daily' | 'weekly' | 'monthly' | 'yearly' = 'daily'
): Promise<DailyHoroscopeRecord[]> {
  return requestJson<DailyHoroscopeRecord[]>(`${API_BASE_URL}/horoscope/${period}`);
}

export interface PanchangTithi {
  tithi_number: number;
  name: string;
  paksha: string;
  full_name: string;
  progress_percent: number;
}

export interface PanchangNakshatra {
  nakshatra_id: number;
  name: string;
  pada: number;
  lord: string;
  progress_percent: number;
}

export interface PanchangYoga {
  yoga_number: number;
  name: string;
  progress_percent: number;
}

export interface PanchangKarana {
  karana_number: number;
  name: string;
  is_fixed: boolean;
  progress_percent: number;
}

export interface PanchangVara {
  day_number: number;
  english_name: string;
  sanskrit_name: string;
  ruler: string;
}

export interface MuhurtaWindow {
  name: string;
  type: 'auspicious' | 'inauspicious';
  start: string;
  end: string;
  display: string;
  is_wednesday_avoid?: boolean;
}

export interface DailyPanchangRecord {
  date: string;
  location: {
    latitude: number;
    longitude: number;
    timezone: string;
  };
  ayanamsa: string;
  sun_times: {
    sunrise: string;
    sunset: string;
  };
  panchang: {
    tithi: PanchangTithi;
    nakshatra: PanchangNakshatra;
    yoga: PanchangYoga;
    karana: PanchangKarana;
    vara: PanchangVara;
  };
  muhurtas: {
    rahu_kaal: MuhurtaWindow;
    yamaganda: MuhurtaWindow;
    gulika_kaal: MuhurtaWindow;
    abhijit_muhurat: MuhurtaWindow;
  };
}

export async function getDailyPanchang(params?: {
  date?: string;
  lat?: number;
  lon?: number;
  ayanamsa?: string;
}): Promise<DailyPanchangRecord> {
  const query = new URLSearchParams();
  if (params?.date) query.append('date', params.date);
  if (params?.lat !== undefined) query.append('lat', params.lat.toString());
  if (params?.lon !== undefined) query.append('lon', params.lon.toString());
  if (params?.ayanamsa) query.append('ayanamsa', params.ayanamsa);

  const url = `${API_BASE_URL}/panchang/daily?${query.toString()}`;
  return requestJson<DailyPanchangRecord>(url);
}

export interface WesternZodiacResult {
  sign: string;
  symbol: string;
  dateRange: string;
  element: 'Fire' | 'Earth' | 'Air' | 'Water';
  quality: 'Cardinal' | 'Fixed' | 'Mutable';
  rulingPlanet: string;
  traits: string;
  birthstone: string;
  birthFlower: string;
  luckyColor: string;
  luckyDay: string;
  luckyNumbers: number[];
  strengths: string[];
  challenges: string[];
  loveAndRelationships: string;
  bestCompatibility: string[];
  careerAndSuccess: string;
  famousPersonalities: string[];
}

export async function getWesternZodiacSign(month: number, day: number): Promise<WesternZodiacResult> {
  return requestJson<WesternZodiacResult>(
    `${API_BASE_URL}/western-zodiac?month=${month}&day=${day}`
  );
}

export interface QuickFactMoonSign {
  sign_name: string;
  sanskrit_sign_name: string;
  degree_in_sign: number;
  formatted_degree: string;
}

export interface QuickFactAscendant {
  sign_name: string;
  sanskrit_sign_name: string;
  degree_in_sign: number;
  formatted_degree: string;
  nakshatra_name: string;
  nakshatra_pada: number;
}

export interface QuickFactNakshatra {
  nakshatra_name: string;
  pada: number;
  lord: string;
  degrees_in_nakshatra: number;
  moon_degree_in_sign: number;
  formatted_degree: string;
}

export interface QuickFactsResponse {
  moon_sign: QuickFactMoonSign;
  sun_sign: QuickFactMoonSign;
  ascendant: QuickFactAscendant;
  nakshatra: QuickFactNakshatra;
}

export async function calculateMoonSign(input: CreateBirthProfileInput): Promise<QuickFactMoonSign> {
  return requestJson<QuickFactMoonSign>(`${API_BASE_URL}/moon-sign`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      birth_date: input.birthDate,
      birth_time: input.birthTime,
      latitude: input.latitude,
      longitude: input.longitude,
      timezone: input.timezone,
      location_name: input.locationName
    })
  });
}

export async function calculateSunSign(input: CreateBirthProfileInput): Promise<QuickFactMoonSign> {
  return requestJson<QuickFactMoonSign>(`${API_BASE_URL}/sun-sign`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      birth_date: input.birthDate,
      birth_time: input.birthTime,
      latitude: input.latitude,
      longitude: input.longitude,
      timezone: input.timezone,
      location_name: input.locationName
    })
  });
}

export async function calculateAscendant(input: CreateBirthProfileInput): Promise<QuickFactAscendant> {
  return requestJson<QuickFactAscendant>(`${API_BASE_URL}/ascendant`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      birth_date: input.birthDate,
      birth_time: input.birthTime,
      latitude: input.latitude,
      longitude: input.longitude,
      timezone: input.timezone,
      location_name: input.locationName
    })
  });
}

export async function calculateNakshatra(input: CreateBirthProfileInput): Promise<QuickFactNakshatra> {
  return requestJson<QuickFactNakshatra>(`${API_BASE_URL}/nakshatra`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      birth_date: input.birthDate,
      birth_time: input.birthTime,
      latitude: input.latitude,
      longitude: input.longitude,
      timezone: input.timezone,
      location_name: input.locationName
    })
  });
}

export interface NumerologyNumberDetail {
  number: number;
  isMasterNumber: boolean;
  title: string;
  meaning: string;
}

export interface NumerologyResponse {
  lifePathNumber?: NumerologyNumberDetail;
  birthdayNumber?: NumerologyNumberDetail;
  destinyNumber?: NumerologyNumberDetail;
  soulUrgeNumber?: NumerologyNumberDetail;
  personalityNumber?: NumerologyNumberDetail;
}

export async function calculateNumerology(payload: {
  fullName?: string;
  dateOfBirth?: string;
}): Promise<NumerologyResponse> {
  return requestJson<NumerologyResponse>(`${API_BASE_URL}/numerology`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
}

export type NumerologyVerdict = 'Favourable' | 'Best avoided' | 'Neutral';

export interface NumerologyVerdictItem {
  total: number;
  compound: number | null;
  reduced: number;
  planet: string;
  verdict: NumerologyVerdict;
  reason: string;
  radicalNumber: number;
}

export async function calculateMobileNumberNumerology(payload: {
  mobileNumber: string;
  dateOfBirth: string;
}): Promise<NumerologyVerdictItem> {
  return requestJson<NumerologyVerdictItem>(`${API_BASE_URL}/numerology/mobile-number`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
}

export async function calculateVehicleNumberNumerology(payload: {
  vehicleNumber: string;
  dateOfBirth: string;
}): Promise<NumerologyVerdictItem> {
  return requestJson<NumerologyVerdictItem>(`${API_BASE_URL}/numerology/vehicle-number`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
}

export async function calculateHouseNumberNumerology(payload: {
  houseNumber: string;
  dateOfBirth: string;
}): Promise<NumerologyVerdictItem> {
  return requestJson<NumerologyVerdictItem>(`${API_BASE_URL}/numerology/house-number`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
}

export interface BusinessNameNumerologyResult {
  total: number;
  compound: number | null;
  reduced: number;
  planet: string;
  meaning: string;
  system: 'Chaldean' | 'Pythagorean';
  radicalNumber?: number;
  verdict?: NumerologyVerdict;
  reason?: string;
}

export async function calculateBusinessNameNumerology(payload: {
  businessName: string;
  system?: 'Chaldean' | 'Pythagorean';
  dateOfBirth?: string;
}): Promise<BusinessNameNumerologyResult> {
  return requestJson<BusinessNameNumerologyResult>(`${API_BASE_URL}/numerology/business-name`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
}

export interface PersonalYearNumerologyResult {
  personalYear: number;
  dayReduced: number;
  monthReduced: number;
  yearReduced: number;
  theme: string;
  targetYear: number;
}

export async function calculatePersonalYearNumerology(payload: {
  birthDay: number;
  birthMonth: number;
  targetYear?: number;
}): Promise<PersonalYearNumerologyResult> {
  return requestJson<PersonalYearNumerologyResult>(`${API_BASE_URL}/numerology/personal-year`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
}

export interface LoShuGridResult {
  grid: Record<number, number>;
  present: number[];
  missing: number[];
  repeated: number[];
  meanings: Record<number, { present: string; missing: string; repeated: string }>;
}

export async function calculateLoShuGrid(payload: {
  dateOfBirth: string;
}): Promise<LoShuGridResult> {
  return requestJson<LoShuGridResult>(`${API_BASE_URL}/numerology/lo-shu-grid`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
}

export interface NameCorrectionNumerologyResult {
  currentNumber: number;
  compound: number | null;
  total: number;
  planet: string;
  system: 'Chaldean' | 'Pythagorean';
  radicalNumber: number;
  radicalVerdict: NumerologyVerdict;
  radicalReason: string;
  lifePathNumber: number;
  lifePathVerdict: NumerologyVerdict;
  lifePathReason: string;
  originalMasterNumber?: number | null;
  favourableNumbers: number[];
  guidanceNote: string;
}

export async function calculateNameCorrectionNumerology(payload: {
  name: string;
  dateOfBirth: string;
  system?: 'Chaldean' | 'Pythagorean';
}): Promise<NameCorrectionNumerologyResult> {
  return requestJson<NameCorrectionNumerologyResult>(`${API_BASE_URL}/numerology/name-correction`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
}

export interface MangalDoshaResult {
  isManglik: boolean;
  houseFromLagna: number | null;
  houseFromMoon: number | null;
  isManglikFromLagna: boolean;
  isManglikFromMoon: boolean;
  severity: string;
  description: string;
  explanation: string;
}

export async function calculateMangalDosha(input: CreateBirthProfileInput): Promise<MangalDoshaResult> {
  return requestJson<MangalDoshaResult>(`${API_BASE_URL}/mangal-dosha`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      birth_date: input.birthDate,
      birth_time: input.birthTime,
      latitude: input.latitude,
      longitude: input.longitude,
      timezone: input.timezone,
      location_name: input.locationName
    })
  });
}

export interface AntardashaPeriod {
  sub_lord: string;
  start_date: string;
  end_date: string;
  duration_years: number;
  is_current?: boolean;
}

export interface MahadashaPeriod {
  lord: string;
  start_date: string;
  end_date: string;
  duration_years: number;
  is_current?: boolean;
  antardashas: AntardashaPeriod[];
}

export interface DashaTimelineResponse {
  birth_moon_longitude: number;
  birth_nakshatra: string;
  birth_nakshatra_lord: string;
  elapsed_fraction: number;
  remaining_fraction: number;
  reference_date: string;
  current_mahadasha?: {
    lord: string;
    start_date: string;
    end_date: string;
    duration_years: number;
    current_antardasha?: {
      lord: string;
      start_date: string;
      end_date: string;
      duration_years: number;
    };
  };
  mahadashas: MahadashaPeriod[];
}

export async function calculateDasha(input: CreateBirthProfileInput, targetDate?: string): Promise<DashaTimelineResponse> {
  let url = `${API_BASE_URL}/dasha`;
  if (targetDate) {
    url += `?targetDate=${encodeURIComponent(targetDate)}`;
  }
  return requestJson<DashaTimelineResponse>(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      birth_date: input.birthDate,
      birth_time: input.birthTime,
      latitude: input.latitude,
      longitude: input.longitude,
      timezone: input.timezone,
      location_name: input.locationName
    })
  });
}

export interface FlamesStep {
  step: number;
  eliminatedLetter: string;
  remainingLetters: string[];
}

export interface FlamesResult {
  nameA: string;
  nameB: string;
  uncancelledLettersCount: number;
  resultLetter: string;
  meaning: string;
  description: string;
  eliminationSteps: FlamesStep[];
}

export async function calculateFlames(nameA: string, nameB: string): Promise<FlamesResult> {
  return requestJson<FlamesResult>(`${API_BASE_URL}/flames`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ nameA, nameB })
  });
}

export interface ZodiacCategoryDetail {
  percent: number;
  blurb: string;
}

export interface ZodiacPairCompatibilityResult {
  signA: string;
  signB: string;
  overallLove: number;
  categories: {
    love: ZodiacCategoryDetail;
    communication: ZodiacCategoryDetail;
    friendship: ZodiacCategoryDetail;
    sexual: ZodiacCategoryDetail;
  };
}

export type ZodiacMatrixGridResult = Record<string, Record<string, number>>;

export async function calculateZodiacCompatibility(signA: string, signB: string): Promise<ZodiacPairCompatibilityResult> {
  const url = `${API_BASE_URL}/zodiac-compatibility?signA=${encodeURIComponent(signA)}&signB=${encodeURIComponent(signB)}`;
  return requestJson<ZodiacPairCompatibilityResult>(url);
}

export async function getZodiacCompatibilityMatrix(): Promise<ZodiacMatrixGridResult> {
  return requestJson<ZodiacMatrixGridResult>(`${API_BASE_URL}/zodiac-compatibility/matrix`);
}

export interface KaalSarpResult {
  is_kaal_sarp_dosh: boolean;
  is_partial_kaal_sarp: boolean;
  dosh_type: string | null;
  house_of_rahu: number;
  alignment_type?: string;
  type_id?: number | null;
  type_name?: string | null;
  description: string;
  outlier_planets: string[];
  rahu_longitude?: number;
  ketu_longitude?: number;
  rahu_arc_planets?: string[];
  ketu_arc_planets?: string[];
}

export async function calculateKaalSarp(input: CreateBirthProfileInput): Promise<KaalSarpResult> {
  return requestJson<KaalSarpResult>(`${API_BASE_URL}/kaal-sarp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      birth_date: input.birthDate,
      birth_time: input.birthTime,
      latitude: input.latitude,
      longitude: input.longitude,
      timezone: input.timezone,
      location_name: input.locationName
    })
  });
}

export interface SadeSatiResult {
  is_sade_sati_active: boolean;
  current_phase: string | null;
  phase_info?: { name: string; description: string } | null;
  natal_moon_sign: string;
  natal_moon_sign_id: number;
  transiting_saturn_sign: string;
  transiting_saturn_sign_id: number;
  phase_start_date: string | null;
  phase_end_date: string | null;
  overall_cycle_start_date: string | null;
  overall_cycle_end_date: string | null;
  next_onset_approx_year: number | null;
  description: string;
}

export async function calculateSadeSati(input: CreateBirthProfileInput, targetDate?: string): Promise<SadeSatiResult> {
  let url = `${API_BASE_URL}/sade-sati`;
  if (targetDate) url += `?targetDate=${encodeURIComponent(targetDate)}`;

  return requestJson<SadeSatiResult>(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      birth_date: input.birthDate,
      birth_time: input.birthTime,
      latitude: input.latitude,
      longitude: input.longitude,
      timezone: input.timezone,
      location_name: input.locationName
    })
  });
}

export interface TransitOverlayItem {
  planet: string;
  longitude: number;
  sign_id: number;
  sign_name: string;
  degree_in_sign: number;
  formatted_degree: string;
  is_retrograde: boolean;
  natal_house: number;
  conjunct_natal_planets: string[];
}

export interface NotableConjunction {
  transiting_planet: string;
  natal_planet: string;
  sign_id: number;
  sign_name: string;
  natal_house: number;
}

export interface TransitChartResult {
  transit_date: string;
  natal_ascendant_sign: string;
  natal_ascendant_sign_id: number;
  is_saturn_return: boolean;
  saturn_return_details: {
    sign_id: number;
    sign_name: string;
    natal_house: number;
    description: string;
  } | null;
  transit_overlays: TransitOverlayItem[];
  notable_conjunctions?: Array<{
    transiting_planet: string;
    natal_planet: string;
    sign_id: number;
    sign_name: string;
    natal_house: number;
  }>;
  scope_disclaimer: string;
}

export async function calculateTransitChart(input: CreateBirthProfileInput, transitDate?: string): Promise<TransitChartResult> {
  let url = `${API_BASE_URL}/transit-chart`;
  if (transitDate) url += `?transitDate=${encodeURIComponent(transitDate)}`;

  return requestJson<TransitChartResult>(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      birth_date: input.birthDate,
      birth_time: input.birthTime,
      latitude: input.latitude,
      longitude: input.longitude,
      timezone: input.timezone,
      location_name: input.locationName
    })
  });
}

export interface AshtakavargaSignBreakdown {
  sign_id: number;
  sign_name: string;
  total_bindus: number;
  graha_bindus: Record<string, number>;
}

export interface AshtakavargaResult {
  ascendant_sign: string;
  ascendant_sign_id: number;
  bhinnashtakavarga: Record<string, number[]>;
  sarvashtakavarga: number[];
  graha_totals: Record<string, number>;
  grand_total: number;
  sign_breakdowns: AshtakavargaSignBreakdown[];
}

export async function calculateAshtakavarga(input: CreateBirthProfileInput): Promise<AshtakavargaResult> {
  return requestJson<AshtakavargaResult>(`${API_BASE_URL}/ashtakavarga`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      birth_date: input.birthDate,
      birth_time: input.birthTime,
      latitude: input.latitude,
      longitude: input.longitude,
      timezone: input.timezone,
      location_name: input.locationName
    })
  });
}

export interface ShadbalaPlanetResult {
  planet: string;
  sign_name: string;
  house: number;
  is_retrograde: boolean;
  sthana_bala_virupas: number;
  dig_bala_virupas: number;
  kala_bala_virupas: number;
  chesta_bala_virupas: number;
  naisargika_bala_virupas: number;
  drik_bala_virupas: number;
  total_shadbala_virupas: number;
  total_shadbala_rupas: number;
  required_minimum_rupas: number;
  required_minimum_virupas: number;
  strength_ratio: number;
  classification: string;
  drik_details?: any;
}

export interface ShadbalaResult {
  is_partial: boolean;
  components_included: string[];
  components_pending: string[];
  unit_consistency_note: string;
  results: ShadbalaPlanetResult[];
}

export async function calculateShadbala(input: CreateBirthProfileInput): Promise<ShadbalaResult> {
  return requestJson<ShadbalaResult>(`${API_BASE_URL}/shadbala`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      birth_date: input.birthDate,
      birth_time: input.birthTime,
      latitude: input.latitude,
      longitude: input.longitude,
      timezone: input.timezone,
      location_name: input.locationName
    })
  });
}

export interface YantraItem {
  id: string;
  planet: string;
  sanskritName: string;
  deity: string;
  gridType: 'numeral_magic_square' | 'symbolic_geometric';
  numeralSquare?: {
    grid: number[][];
    magicConstant: number;
  };
  symbolicFormDescription?: string;
  purposeDescription: string;
  mantra: string;
  elementColor: string;
}

export interface PlanetRatioItem {
  planet: string;
  totalRupas: number;
  requiredMinimumRupas: number;
  strengthRatio: number;
  classification: string;
}

export interface YantraRecommendationResult {
  recommendedPlanet: string;
  recommendedYantra: YantraItem;
  reason: string;
  allPlanetRatios: PlanetRatioItem[];
}

export async function getYantraLibrary(): Promise<YantraItem[]> {
  return requestJson<YantraItem[]>(`${API_BASE_URL}/yantra/library`);
}

export async function recommendYantra(input: CreateBirthProfileInput): Promise<YantraRecommendationResult> {
  return requestJson<YantraRecommendationResult>(`${API_BASE_URL}/yantra/recommend`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      birth_date: input.birthDate,
      birth_time: input.birthTime,
      latitude: input.latitude,
      longitude: input.longitude,
      timezone: input.timezone,
      location_name: input.locationName
    })
  });
}

export async function geocodeLocation(query: string): Promise<{ name: string; latitude: number; longitude: number; timezone: string }[]> {
  return requestJson<{ name: string; latitude: number; longitude: number; timezone: string }[]>(
    `${API_BASE_URL}/geocode?q=${encodeURIComponent(query)}`
  );
}

export async function requestPasswordResetApi(email: string): Promise<{ success: boolean; message: string }> {
  return requestJson<{ success: boolean; message: string }>(`${API_BASE_URL}/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email })
  });
}

export async function confirmPasswordResetApi(token: string, newPassword: string): Promise<{ success: boolean; message: string }> {
  return requestJson<{ success: boolean; message: string }>(`${API_BASE_URL}/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, newPassword })
  });
}

export async function submitContactFormApi(data: { name?: string; email: string; subject?: string; message: string }): Promise<{ success: boolean; message: string }> {
  return requestJson<{ success: boolean; message: string }>(`${API_BASE_URL}/contact`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
}

export async function getChatUsageApi(): Promise<{ usageCount: number; limit: number; remaining: number; isLimitReached: boolean }> {
  return requestJson<{ usageCount: number; limit: number; remaining: number; isLimitReached: boolean }>(`${API_BASE_URL}/ai/chat/usage`);
}

export async function sendChatMessageApi(payload: {
  question: string;
  facts: any;
  history?: { role: 'user' | 'assistant'; content: string }[];
}): Promise<{ reply: string; usageCount: number; limit: number; remaining: number }> {
  return requestJson<{ reply: string; usageCount: number; limit: number; remaining: number }>(`${API_BASE_URL}/ai/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
}

export interface NadiDoshaResult {
  score: number;
  maxScore: number;
  description: string;
  dosha: boolean;
  exceptionApplies: boolean;
  isCompatible: boolean;
  nadi_a?: string;
  nadi_b?: string;
  reason_code?: string;
}

export async function calculateNadiDosha(payload: { personA?: CreateBirthProfileInput; personB?: CreateBirthProfileInput; nakshatraA?: number; nakshatraB?: number; signA?: number; signB?: number }): Promise<NadiDoshaResult> {
  return requestJson<NadiDoshaResult>(`${API_BASE_URL}/matching/nadi-dosha`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      personA: payload.personA ? {
        birth_date: payload.personA.birthDate,
        birth_time: payload.personA.birthTime,
        latitude: payload.personA.latitude,
        longitude: payload.personA.longitude,
        timezone: payload.personA.timezone
      } : undefined,
      personB: payload.personB ? {
        birth_date: payload.personB.birthDate,
        birth_time: payload.personB.birthTime,
        latitude: payload.personB.latitude,
        longitude: payload.personB.longitude,
        timezone: payload.personB.timezone
      } : undefined,
      nakshatraA: payload.nakshatraA,
      nakshatraB: payload.nakshatraB,
      signA: payload.signA,
      signB: payload.signB
    })
  });
}

export async function getRahuKaalCalculatorApi(date?: string, lat = 28.6139, lon = 77.2090, tz = "Asia/Kolkata"): Promise<any> {
  const query = new URLSearchParams({ lat: lat.toString(), lon: lon.toString(), timezone: tz });
  if (date) query.set("date", date);
  return requestJson<any>(`${API_BASE_URL}/panchang/rahu-kaal?${query.toString()}`);
}

export async function getChoghadiyaCalculatorApi(date?: string, lat = 28.6139, lon = 77.2090, tz = "Asia/Kolkata"): Promise<any> {
  const query = new URLSearchParams({ lat: lat.toString(), lon: lon.toString(), timezone: tz });
  if (date) query.set("date", date);
  return requestJson<any>(`${API_BASE_URL}/panchang/choghadiya?${query.toString()}`);
}

export async function getHoraCalculatorApi(date?: string, lat = 28.6139, lon = 77.2090, tz = "Asia/Kolkata"): Promise<any> {
  const query = new URLSearchParams({ lat: lat.toString(), lon: lon.toString(), timezone: tz });
  if (date) query.set("date", date);
  return requestJson<any>(`${API_BASE_URL}/panchang/hora?${query.toString()}`);
}

export async function getAyanamsaComparisonApi(date?: string): Promise<any> {
  const query = new URLSearchParams();
  if (date) query.set("date", date);
  return requestJson<any>(`${API_BASE_URL}/ayanamsa-comparison?${query.toString()}`);
}

export interface NavamsaPlanetResult {
  name: string;
  id: number;
  longitude: number;
  d1_sign_id: number;
  d1_sign_name: string;
  d1_sanskrit_sign_name: string;
  d1_degree_in_sign: number;
  d1_formatted_degree: string;
  d1_house: number;
  d9_sign_id: number;
  d9_sign_name: string;
  d9_sanskrit_sign_name: string;
  d9_house: number;
  is_vargottama: boolean;
  is_retrograde: boolean;
  is_combust: boolean;
  nakshatra_name: string;
  nakshatra_pada: number;
}

export interface NavamsaHouseResult {
  house: number;
  sign_id: number;
  sign_name: string;
  sanskrit_sign_name: string;
  occupants: string[];
}

export interface NavamsaChartResult {
  birth_details: CreateBirthProfileInput;
  navamsa_lagna: {
    d1_sign_id: number;
    d1_sign_name: string;
    d1_degree_in_sign: number;
    d1_formatted_degree: string;
    d9_sign_id: number;
    d9_sign_name: string;
    d9_sanskrit_sign_name: string;
    is_vargottama: boolean;
  };
  planets: NavamsaPlanetResult[];
  vargottama_planets: string[];
  d9_houses: NavamsaHouseResult[];
  summary: string;
}

export async function calculateNavamsaChart(input: CreateBirthProfileInput): Promise<NavamsaChartResult> {
  return requestJson<NavamsaChartResult>(`${API_BASE_URL}/navamsa-chart`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      birth_date: input.birthDate,
      birth_time: input.birthTime,
      latitude: input.latitude,
      longitude: input.longitude,
      timezone: input.timezone,
      location_name: input.locationName,
      ayanamsa: input.ayanamsa || 'lahiri'
    })
  });
}

export interface BirthPanchangResult {
  birth_details: CreateBirthProfileInput;
  sun_times: {
    sunrise: string;
    sunset: string;
    is_born_before_sunrise: boolean;
    vara_boundary_note: string;
  };
  panchang: {
    tithi: {
      tithi_number: number;
      name: string;
      paksha: string;
      full_name: string;
      progress_percent: number;
    };
    nakshatra: {
      nakshatra_id: number;
      name: string;
      pada: number;
      lord: string;
      degrees_in_nakshatra: number;
      progress_percent: number;
    };
    yoga: {
      yoga_number: number;
      name: string;
      progress_percent: number;
    };
    karana: {
      karana_number: number;
      name: string;
      is_fixed: boolean;
      progress_percent: number;
    };
    vara: {
      day_number: number;
      english_name: string;
      sanskrit_name: string;
      ruler: string;
      is_pre_sunrise_adjustment: boolean;
    };
  };
  muhurtas: {
    rahu_kaal: { name: string; type: string; start: string; end: string; display: string };
    yamaganda: { name: string; type: string; start: string; end: string; display: string };
    gulika_kaal: { name: string; type: string; start: string; end: string; display: string };
    abhijit_muhurat: { name: string; type: string; start: string; end: string; display: string; is_wednesday_avoid?: boolean };
  };
  summary: string;
}

export async function calculateBirthPanchang(input: CreateBirthProfileInput): Promise<BirthPanchangResult> {
  return requestJson<BirthPanchangResult>(`${API_BASE_URL}/birth-panchang`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      birth_date: input.birthDate,
      birth_time: input.birthTime,
      latitude: input.latitude,
      longitude: input.longitude,
      timezone: input.timezone,
      location_name: input.locationName,
      ayanamsa: input.ayanamsa || 'lahiri'
    })
  });
}

export interface SingleMoonPhaseData {
  date: string;
  time: string;
  elongation_degrees: number;
  illumination_percentage: number;
  moon_age_days: number;
  phase_code: string;
  phase_name: string;
  phase_description: string;
  paksha: string;
  tithi_name: string;
  tithi_number: number;
  moon_sign: string;
  moon_sanskrit_sign: string;
  sun_longitude: number;
  moon_longitude: number;
}

export interface MoonPhaseResult {
  primary: SingleMoonPhaseData;
  comparison?: SingleMoonPhaseData | null;
  summary: string;
}

export interface MoonPhaseCalculatorInput {
  birthDate: string;
  birthTime?: string;
  latitude?: number;
  longitude?: number;
  timezone?: string;
  compareDate?: string;
}

export async function calculateMoonPhase(input: MoonPhaseCalculatorInput): Promise<MoonPhaseResult> {
  return requestJson<MoonPhaseResult>(`${API_BASE_URL}/moon-phase`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      birth_date: input.birthDate,
      birth_time: input.birthTime || '12:00:00',
      latitude: input.latitude || 28.6139,
      longitude: input.longitude || 77.2090,
      timezone: input.timezone || 'Asia/Kolkata',
      compare_date: input.compareDate || null
    })
  });
}

export interface BabyNamePadaDetail {
  pada: number;
  syllable: string;
  devanagari: string;
  is_selected_pada: boolean;
  suggested_names: {
    boy: string[];
    girl: string[];
  };
  devanagari_names?: {
    boy: string[];
    girl: string[];
  };
}

export interface BabyNameResult {
  mode: string;
  moon_sign?: string;
  moon_sign_id?: number;
  moon_sanskrit_sign?: string;
  nakshatra_id?: number;
  nakshatra_name?: string;
  selected_pada?: number;
  primary_syllable?: string;
  primary_devanagari?: string;
  padas?: BabyNamePadaDetail[];
  primary_suggested_names?: {
    boy: string[];
    girl: string[];
  };
  primary_devanagari_names?: {
    boy: string[];
    girl: string[];
  };
  nakshatras_in_sign?: Array<{
    nakshatra_id: number;
    nakshatra_name: string;
    selected_pada: number;
    primary_syllable: string;
    primary_devanagari: string;
    padas: BabyNamePadaDetail[];
    primary_suggested_names: {
      boy: string[];
      girl: string[];
    };
    primary_devanagari_names?: {
      boy: string[];
      girl: string[];
    };
  }>;
  summary: string;
}

export interface BabyNameCalculatorInput {
  birthDate?: string;
  birthTime?: string;
  latitude?: number;
  longitude?: number;
  timezone?: string;
  nakshatraId?: number;
  pada?: number;
  moonSignId?: number;
}

export async function calculateBabyName(input: BabyNameCalculatorInput): Promise<BabyNameResult> {
  const payload: any = {};
  if (input.birthDate && input.birthTime && input.latitude !== undefined && input.longitude !== undefined) {
    payload.birth_details = {
      birth_date: input.birthDate,
      birth_time: input.birthTime,
      latitude: input.latitude,
      longitude: input.longitude,
      timezone: input.timezone || 'Asia/Kolkata',
      ayanamsa: 'lahiri'
    };
  } else if (input.nakshatraId !== undefined) {
    payload.nakshatra_id = input.nakshatraId;
    payload.pada = input.pada || 1;
  } else if (input.moonSignId !== undefined) {
    payload.moon_sign_id = input.moonSignId;
  }

  return requestJson<BabyNameResult>(`${API_BASE_URL}/baby-name`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
}

export interface CharaKarakaItem {
  rank: number;
  karaka_code: string;
  karaka_name: string;
  significance: string;
  description: string;
  planet: string;
  sign_name: string;
  sanskrit_sign_name: string;
  degree_in_sign: number;
  formatted_degree: string;
  house: number;
  nakshatra_name: string;
  nakshatra_pada: number;
  is_retrograde: boolean;
  is_combust: boolean;
}

export interface AtmakarakaResult {
  scheme: string;
  excludes_rahu: boolean;
  tie_detected: boolean;
  atmakaraka: CharaKarakaItem;
  darakaraka: CharaKarakaItem;
  karakas: CharaKarakaItem[];
  birth_details: {
    birth_date: string;
    birth_time: string;
    latitude: number;
    longitude: number;
    timezone: string;
    ayanamsa: string;
  };
  summary: string;
}

export interface AtmakarakaCalculatorInput {
  birthDate: string;
  birthTime: string;
  latitude: number;
  longitude: number;
  timezone?: string;
  ayanamsa?: string;
}

export async function calculateAtmakaraka(input: AtmakarakaCalculatorInput): Promise<AtmakarakaResult> {
  return requestJson<AtmakarakaResult>(`${API_BASE_URL}/atmakaraka`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      birth_date: input.birthDate,
      birth_time: input.birthTime,
      latitude: input.latitude,
      longitude: input.longitude,
      timezone: input.timezone || 'Asia/Kolkata',
      ayanamsa: input.ayanamsa || 'lahiri'
    })
  });
}

export interface PitraDoshaIndication {
  code: string;
  title: string;
  description: string;
  severity: string;
}

export interface PitraDoshaCancellation {
  code: string;
  title: string;
  description: string;
}

export interface PitraDoshaResult {
  status: string;
  status_code: 'NONE' | 'MITIGATED' | 'MODERATE' | 'STRONG';
  total_indications: number;
  total_cancellations: number;
  indications: PitraDoshaIndication[];
  cancellations: PitraDoshaCancellation[];
  ninth_house_sign: number;
  ninth_lord: string;
  disclaimer: string;
  summary: string;
}

export interface PitraDoshaCalculatorInput {
  birthDate: string;
  birthTime: string;
  latitude: number;
  longitude: number;
  timezone?: string;
  ayanamsa?: string;
}

export async function calculatePitraDosha(input: PitraDoshaCalculatorInput): Promise<PitraDoshaResult> {
  return requestJson<PitraDoshaResult>(`${API_BASE_URL}/pitra-dosha`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      birth_date: input.birthDate,
      birth_time: input.birthTime,
      latitude: input.latitude,
      longitude: input.longitude,
      timezone: input.timezone || 'Asia/Kolkata',
      ayanamsa: input.ayanamsa || 'lahiri'
    })
  });
}

export interface LovePersonDetail {
  name: string;
  syllable: string;
  devanagari: string;
  nakshatra_id: number;
  nakshatra_name: string;
  pada: number;
  sign: string;
}

export interface LoveKootaItem {
  name: string;
  score: number;
  max_score: number;
  significance: string;
  description: string;
}

export interface LoveCalculatorResult {
  is_symmetric: boolean;
  person1: LovePersonDetail;
  person2: LovePersonDetail;
  score: number;
  max_score: number;
  percentage: number;
  verdict: string;
  verdict_badge: string;
  kootas: LoveKootaItem[];
  nadi_dosha: boolean;
  nadi_exception_applied: boolean;
  bhakoot_dosha: boolean;
  disclaimer: string;
  summary: string;
}

export interface LoveCalculatorInput {
  name1: string;
  name2: string;
}

export async function calculateLoveScore(input: LoveCalculatorInput): Promise<LoveCalculatorResult> {
  return requestJson<LoveCalculatorResult>(`${API_BASE_URL}/love-calculator`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name1: input.name1,
      name2: input.name2
    })
  });
}
